import type { Textbox } from '../stores/EditorStore'

/** Single source of truth for the textBoxesJson wrapper `version` field (v2 client). */
export const SHOOTS_SCHEMA_VERSION = 2

/**
 * Fixed reference height (px) for the shoots **font size slider** (Option A).
 * `Textbox.fontSize` is stored as a **fraction of the shoot’s actual media height** (see EditorStore).
 * The slider still shows familiar values 14–72; those numbers mean “CSS px font height **if** the media were
 * `SHOOTS_FONT_SLIDER_REFERENCE_HEIGHT_PX` pixels tall”: `fontSize = sliderValue / SHOOTS_FONT_SLIDER_REFERENCE_HEIGHT_PX`.
 * That keeps the same thumb position ≈ the same visual size across different intrinsic heights.
 */
export const SHOOTS_FONT_SLIDER_REFERENCE_HEIGHT_PX = 720

/** Close control size in CSS pixels (editor overlay). */
export const SHOOTS_CLOSE_BTN_PX = 22
export const SHOOTS_CLOSE_PAD_PX = 4

export const SHOOTS_COORD_SPACE = 'normalized' as const

/** One textbox as stored in a v2 `boxes` array (normalized fractions; rotation in radians). */
export interface ShootsTextBoxV2 {
  id: string;
  text: string;
  x: number;
  y: number;
  width: number;
  height: number;
  fontSize: number;
  fontFamily: string;
  color: string;
  opacity: number;
  rotation: number;
}

/** v2 textBoxesJson root object written by the client. */
export interface ShootsTextBoxesV2 {
  version: number;
  coordSpace: typeof SHOOTS_COORD_SPACE;
  mediaAspect: number;
  boxes: ShootsTextBoxV2[];
}

function round6 (n: number): number {
  if (!Number.isFinite(n)) return 0
  return Math.round(n * 1e6) / 1e6
}

function degreesToRadians (deg: number): number {
  return (deg * Math.PI) / 180
}

function radiansToDegrees (rad: number): number {
  return (rad * 180) / Math.PI
}

/**
 * Loads media off-DOM and reads intrinsic pixel dimensions.
 * Photo: `naturalWidth` / `naturalHeight` after load.
 * Video: `videoWidth` / `videoHeight` after `loadedmetadata`.
 */
export function getMediaIntrinsicDimensions (
  file: File,
  mediaType: 'photo' | 'video'
): Promise<{ width: number; height: number }> {
  const url = URL.createObjectURL(file)
  const cleanup = (): void => {
    try {
      URL.revokeObjectURL(url)
    } catch {
      /* ignore */
    }
  }

  if (mediaType === 'photo') {
    return new Promise((resolve, reject) => {
      const img = new Image()
      img.onload = () => {
        const width = img.naturalWidth
        const height = img.naturalHeight
        cleanup()
        if (width > 0 && height > 0) resolve({ width, height })
        else reject(new Error('Image has zero intrinsic dimensions.'))
      }
      img.onerror = () => {
        cleanup()
        reject(new Error('Failed to load image for dimensions.'))
      }
      img.src = url
    })
  }

  return new Promise((resolve, reject) => {
    const video = document.createElement('video')
    video.preload = 'metadata'
    video.muted = true
    video.playsInline = true
    video.onloadedmetadata = () => {
      const width = video.videoWidth
      const height = video.videoHeight
      video.src = ''
      video.removeAttribute('src')
      cleanup()
      if (width > 0 && height > 0) resolve({ width, height })
      else reject(new Error('Video has zero intrinsic dimensions.'))
    }
    video.onerror = () => {
      video.src = ''
      cleanup()
      reject(new Error('Failed to load video for dimensions.'))
    }
    video.src = url
  })
}

/**
 * Rectangle (in container pixel space) where media is drawn under `object-fit: contain`.
 * `mediaAspect` is intrinsic width / height.
 */
export function getObjectFitContainRect (
  containerW: number,
  containerH: number,
  mediaAspect: number
): { x: number; y: number; w: number; h: number } {
  if (!(containerW > 0) || !(containerH > 0) || !Number.isFinite(mediaAspect) || mediaAspect <= 0) {
    return { x: 0, y: 0, w: 0, h: 0 }
  }
  const containerAspect = containerW / containerH
  let w: number
  let h: number
  if (mediaAspect > containerAspect) {
    w = containerW
    h = w / mediaAspect
  } else {
    h = containerH
    w = h * mediaAspect
  }
  const x = (containerW - w) / 2
  const y = (containerH - h) / 2
  return { x, y, w, h }
}

function serializeBox (tb: Textbox): ShootsTextBoxV2 {
  return {
    id: tb.id,
    text: tb.text,
    x: round6(tb.x),
    y: round6(tb.y),
    width: round6(tb.width),
    height: round6(tb.height),
    fontSize: round6(tb.fontSize),
    fontFamily: tb.fontFamily,
    color: tb.color,
    opacity: round6(tb.opacity),
    rotation: round6(degreesToRadians(tb.rotation))
  }
}

/** Builds v2 `textBoxesJson` (normalized boxes; `rotation` on wire in radians). */
export function serializeTextBoxes (boxes: Textbox[], mediaAspect: number): string {
  if (!Number.isFinite(mediaAspect) || mediaAspect <= 0) {
    throw new Error('serializeTextBoxes requires a finite positive mediaAspect (width / height).')
  }
  const aspect = round6(mediaAspect)
  const payload: ShootsTextBoxesV2 = {
    version: SHOOTS_SCHEMA_VERSION,
    coordSpace: SHOOTS_COORD_SPACE,
    mediaAspect: aspect,
    boxes: boxes.map(serializeBox)
  }
  return JSON.stringify(payload)
}

function parseLegacyArrayItem (raw: unknown, index: number): Textbox | null {
  if (!raw || typeof raw !== 'object') return null
  const o = raw as Record<string, unknown>
  const id = o.id != null ? String(o.id) : ''
  const text = o.text != null ? String(o.text) : ''
  if (!id && !text) return null
  const op = o.opacity
  const opacity = op != null && !Number.isNaN(Number(op)) ? Math.min(1, Math.max(0, Number(op))) : 1
  return {
    id: id || `tb-${index}`,
    text,
    x: Number(o.x) || 0,
    y: Number(o.y) || 0,
    width: Number(o.width) || 200,
    height: Number(o.height) || 50,
    fontSize: Math.max(8, Number(o.fontSize) || 20),
    fontFamily: typeof o.fontFamily === 'string' && o.fontFamily ? o.fontFamily : 'Arial, sans-serif',
    color: typeof o.color === 'string' && o.color ? o.color : '#FFFFFF',
    rotation: Number(o.rotation) || 0,
    opacity
  }
}

function parseV2BoxItem (raw: unknown, index: number): Textbox | null {
  if (!raw || typeof raw !== 'object') return null
  const o = raw as Record<string, unknown>
  const id = o.id != null ? String(o.id) : ''
  const text = o.text != null ? String(o.text) : ''
  if (!id && !text) return null
  const op = o.opacity
  const opacity = op != null && !Number.isNaN(Number(op)) ? Math.min(1, Math.max(0, Number(op))) : 1
  const rotRad = Number(o.rotation)
  const rotationDeg = Number.isFinite(rotRad) ? radiansToDegrees(rotRad) : 0
  return {
    id: id || `tb-${index}`,
    text,
    x: Number(o.x) || 0,
    y: Number(o.y) || 0,
    width: Number(o.width) || 0,
    height: Number(o.height) || 0,
    fontSize: Number(o.fontSize) || 0,
    fontFamily: typeof o.fontFamily === 'string' && o.fontFamily ? o.fontFamily : 'Arial',
    color: typeof o.color === 'string' && o.color ? o.color : '#FFFFFF',
    rotation: rotationDeg,
    opacity
  }
}

/**
 * Parses server-stored `textBoxesJson`: legacy v1 root array, or versioned object with `boxes` array.
 * v1: legacy editor pixels / logical coords; `rotation` in degrees.
 * v2+: normalized fractions; wire `rotation` in radians → returned `Textbox.rotation` in degrees (EditorStore).
 */
export function parseTextBoxes (json: string): {
  version: number;
  boxes: Textbox[];
  mediaAspect?: number;
  /** True when the JSON root was a legacy v1 array (not the v2 wrapper object). */
  legacyRootArray?: boolean;
} {
  const trimmed = json?.trim()
  if (!trimmed) return { version: 1, boxes: [] }
  let data: unknown
  try {
    data = JSON.parse(trimmed)
  } catch {
    return { version: 1, boxes: [] }
  }

  if (Array.isArray(data)) {
    const boxes: Textbox[] = []
    data.forEach((raw, i) => {
      const tb = parseLegacyArrayItem(raw, i)
      if (tb) boxes.push(tb)
    })
    return { version: 1, boxes, legacyRootArray: true }
  }

  if (data && typeof data === 'object') {
    const o = data as Record<string, unknown>
    const verRaw = o.version
    const ver = typeof verRaw === 'number' ? verRaw : Number(verRaw)
    const boxesRaw = o.boxes
    if (Number.isFinite(ver) && ver >= 1 && Array.isArray(boxesRaw)) {
      const boxes: Textbox[] = []
      boxesRaw.forEach((raw, i) => {
        const tb = parseV2BoxItem(raw, i)
        if (tb) boxes.push(tb)
      })
      const ma = o.mediaAspect
      const mediaAspect =
        typeof ma === 'number' && Number.isFinite(ma) && ma > 0 ? ma : undefined
      return { version: Math.floor(ver), boxes, mediaAspect }
    }
  }

  return { version: 1, boxes: [] }
}

export interface DrawNormalizedTextboxesOptions {
  editingTextboxId?: string | null;
  /**
   * When true (default), draw editing stroke (editor). Close control is DOM in ShootsEditor.
   * Feed / ShootsCanvasRenderer should pass false.
   */
  showEditorChrome?: boolean;
}

/**
 * Draw shoot textboxes in normalized 0–1 coordinates over a CSS-pixel canvas.
 * Matches ShootsEditor canvas math: positions scale by cssW/cssH, font height = fontSize * cssH,
 * rotation in degrees (Textbox.rotation).
 */
export function drawNormalizedTextboxes (
  ctx: CanvasRenderingContext2D,
  textboxes: readonly Textbox[],
  cssW: number,
  cssH: number,
  options?: DrawNormalizedTextboxesOptions
): void {
  const showChrome = options?.showEditorChrome !== false
  const editingId = options?.editingTextboxId ?? null

  for (const tb of textboxes) {
    const boxLeft = tb.x * cssW
    const boxTop = tb.y * cssH
    const boxW = tb.width * cssW
    const boxH = tb.height * cssH
    const cx = boxLeft + boxW / 2
    const cy = boxTop + boxH / 2

    ctx.save()
    ctx.globalAlpha = tb.opacity
    ctx.translate(cx, cy)
    ctx.rotate((tb.rotation * Math.PI) / 180)
    ctx.translate(-cx, -cy)

    if (showChrome && tb.id === editingId) {
      ctx.strokeStyle = '#ffffff'
      ctx.lineWidth = 2
      ctx.strokeRect(boxLeft, boxTop, boxW, boxH)
    }

    ctx.fillStyle = tb.color
    ctx.font = `${tb.fontSize * cssH}px ${tb.fontFamily}`
    ctx.textAlign = 'left'
    ctx.textBaseline = 'top'
    ctx.fillText(tb.text, boxLeft, boxTop)

    ctx.restore()
  }
}

/** CSS percentage insets when `Textbox` uses normalized 0–1 coordinates. */
export function normalizedRectToCssPercent (tb: Pick<Textbox, 'x' | 'y' | 'width' | 'height'>): {
  left: string;
  top: string;
  width: string;
  height: string;
} {
  return {
    left: `${tb.x * 100}%`,
    top: `${tb.y * 100}%`,
    width: `${tb.width * 100}%`,
    height: `${tb.height * 100}%`
  }
}
